import React, { startTransition } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faPenAlt } from '@fortawesome/pro-regular-svg-icons/faPenAlt';
import { faFlask } from '@fortawesome/pro-regular-svg-icons/faFlask';
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import { faCompress } from '@fortawesome/pro-regular-svg-icons/faCompress';
import { faExpand } from '@fortawesome/pro-regular-svg-icons/faExpand';
import { faHistory } from '@fortawesome/pro-regular-svg-icons/faHistory';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faKeyboard } from '@fortawesome/pro-regular-svg-icons/faKeyboard';
import { faFlag } from '@fortawesome/pro-regular-svg-icons/faFlag';
import { faBan } from '@fortawesome/pro-regular-svg-icons/faBan';
import { faRepeatAlt } from '@fortawesome/pro-regular-svg-icons/faRepeatAlt';

import { useQuizContext } from '../../context/QuizContext';
import useModal from '../../hooks/useModal';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Button } from '@osmosis/react-ui/components/Button';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { IconButton } from '@osmosis/react-ui/components/IconButton';
import { IconButtonProps } from '@osmosis/react-ui/components/IconButton';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Box } from '@osmosis/react-ui/components/Box';

import QuizNavigator from './QuizNavigator';
import KeyboardShortcuts from './KeyboardShortcuts';
import RepetitionSettingsModal from './repetitionSetting/RepetitionSettingsModal';
import ActionMenu from '../PopupMenu';
import ActionMenuRow from '../PopupMenuRow';
import { useQuizShortcutsContext } from '../../context/QuizShortcutsContext';
import { H4 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import Divider from '../Divider';
import { QuizSettings } from '../../lib/shared/quiz/quizSetting';
import { usePagesContext } from '../../context/PagesProvider';
import { useUserContext } from '../../context/UserContext';
import Image from 'next/image';
import ProgressBarBlue from '../../assets/progress-blue.svg';
import ProgressBarGrey from '../../assets/progress-grey.svg';
import { text4 } from '@osmosis/tailwind-ui/styles';

const IconButtonControl = (
  p: IconButtonProps & { isHidden?: boolean },
): JSX.Element | null => {
  const colors = useColors();

  if (p.isHidden) {
    return null;
  }

  return (
    <>
      <IconButton
        m={2}
        iconSize={18}
        color="gray40"
        variant="transparent"
        aria-label={p?.label}
        aria-checked={p?.checked}
        role={p?.role}
        data-testid={p?.['data-testid'] || ''}
        data-tooltip-id={p?.tooltipText && `quiz-btn.tooltip-${p.tooltipText}`}
        {...p}
      />

      {p?.tooltipText && (
        <Tooltip
          id={`quiz-btn.tooltip-${p.tooltipText}`}
          place="bottom"
          style={{
            backgroundColor: colors.text,
            color: colors.textInvert,
            zIndex: ZIndex.MODAL,
          }}
        >
          <P color="inherit" fontSize={13}>
            {p.tooltipText}
          </P>
        </Tooltip>
      )}
    </>
  );
};

type Props = {
  onCloseLockoutModal?: () => void;
};

/**
 * QuizHeader.
 */
const QuizHeader = ({ onCloseLockoutModal }: Props): JSX.Element => {
  const {
    showProgress,
    setShowProgress,
    showLab,
    setShowLab,
    highlighting,
    setHighlighting,
    fullQuizSize,
    setFullQuizSize,
    setShowRepetitionSettings,
    currentItem,
  } = useQuizContext();
  const { user } = useUserContext();

  const { quizSettings, updateQuizSetting } = useQuizContext();
  const intl = useIntl();
  const flag = quizSettings?.[QuizSettings.FLASHCARDS_FLAG_VISIBLE];
  const quizButtonsReversed = quizSettings?.[QuizSettings.BUTTONS_REVERSED];
  const noRepeat = quizSettings?.[QuizSettings.FLASHCARDS_NO_REPEAT];

  const { setShowKeyboardShortcuts, shortcutsEnabled, setShortcutsEnabled } =
    useQuizShortcutsContext();

  const { back, push, refresh, pathname } = usePagesContext();

  const handleExitQuiz = () => {
    startTransition(() => {
      if (pathname?.includes('/learn-quiz')) {
        const learn = pathname.split('/')[2];
        push(`/learn/${learn}`);
      } else if (pathname?.includes('/daily-practice')) {
        push(`/home/dashboard`);
        refresh();
      } else {
        back();
      }
    });
  };

  const {
    isVisible: settingsMenuVisible,
    hide,
    toggle: toggledSettingsMenu,
  } = useModal();

  const hideSettingsMenu = () => {
    setShowKeyboardShortcuts(false);
    hide();
  };

  const handleShortcutsModal = () => {
    setShowKeyboardShortcuts(true);
  };

  const handleFlag = () => {
    updateQuizSetting(QuizSettings.FLASHCARDS_FLAG_VISIBLE, !flag);
  };

  const handleNoRepeat = () => {
    updateQuizSetting(QuizSettings.FLASHCARDS_NO_REPEAT, !noRepeat);
  };

  const handleConfidenceOrder = () => {
    updateQuizSetting(QuizSettings.BUTTONS_REVERSED, !quizButtonsReversed);
  };

  const handleShortcutsToggle = () => {
    setShortcutsEnabled(!shortcutsEnabled);
  };

  const settingsPopupMenuItems = [
    {
      id: 'repetition',
      text: intl.formatMessage({
        id: 'quiz_header.settings.repetition_settings',
        defaultMessage: 'Repetition settings',
      }),
      icon: faHistory,
      onClick: () => {
        setShowRepetitionSettings(true);
        hideSettingsMenu();
      },
    },
    {
      id: 'reverse',
      text: intl.formatMessage({
        id: 'quiz_header.settings.reverse',
        defaultMessage: 'Reverse confidence order',
      }),
      icon: faRepeatAlt,
      toggle: true,
      checked: quizButtonsReversed,
      onClick: handleConfidenceOrder,
      popupItemIsFocusable: -1,
    },
    {
      id: 'divider',
      component: <Divider cn="my-2" />,
    },
    {
      id: 'show',
      component: (
        <Flex
          mb={2}
          mt={3}
          variant="transparent"
          color="text"
          width="100%"
          sx={{
            justifyContent: 'left',
            display: 'flex',
          }}
        >
          <H4 sx={{ marginLeft: '45px' }}>
            <FormattedMessage id="show.text" defaultMessage="Show" />
          </H4>
        </Flex>
      ),
    },
    {
      id: 'flag',
      text: intl.formatMessage({
        id: 'quiz_header.settings.flag',
        defaultMessage: 'Flag',
      }),
      icon: faFlag,
      toggle: true,
      checked: flag,
      onClick: handleFlag,
      popupItemIsFocusable: -1,
    },
    {
      id: 'donotrepeat',
      text: intl.formatMessage({
        id: 'quiz_header.settings.do_not_repeat',
        defaultMessage: 'Do not repeat',
      }),
      icon: faBan,
      toggle: true,
      checked: noRepeat,
      onClick: handleNoRepeat,
      popupItemIsFocusable: -1,
    },
    {
      id: 'divider-1',
      component: <Divider cn="my-2" />,
    },
    {
      id: 'shortcuts',
      text: intl.formatMessage({
        id: 'quiz_header.settings.enable_shortcuts',
        defaultMessage: 'Enable shortcuts',
      }),
      icon: faKeyboard,
      toggle: true,
      checked: shortcutsEnabled,
      onClick: handleShortcutsToggle,
      popupItemIsFocusable: -1,
    },
    {
      id: 'keyboard',
      text: intl.formatMessage({
        id: 'quiz_header.settings.keyboard_shortcuts',
        defaultMessage: 'View keyboard shortcuts',
      }),
      icon: faEye,
      onClick: handleShortcutsModal,
    },
  ].filter(
    (item) =>
      currentItem?.subtype === 5 ||
      !['divider', 'repetition', 'show', 'flag', 'donotrepeat'].includes(
        item.id,
      ),
  );

  return (
    <Box
      data-testid="quiz-header"
      sx={{ borderBottom: '1px solid', borderColor: 'gray10', p: '5px' }}
    >
      <Flex
        onKeyDown={(e) => {
          if (e.key === 'Space') {
            if (settingsMenuVisible) {
              e.preventDefault();
            }
          }
        }}
        width={1}
        height={56}
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex flex={1}>
          <IconButtonControl
            icon={<Icon size="lg" icon={faArrowLeft} />}
            onClick={handleExitQuiz}
            tooltipText={intl.formatMessage({
              id: 'quiz_tooltip.exit',
              defaultMessage: 'Exit Quiz',
            })}
            data-testid="quiz-exit-btn"
            aria-label={intl.formatMessage({
              id: 'quiz-exit-btn',
              defaultMessage: 'Quiz Exit button',
            })}
          />

          <IconButtonControl
            data-testid="progress-toggle"
            onClick={() => setShowProgress(!showProgress)}
            tooltipText={intl.formatMessage({
              id: 'quiz_tooltip.progress',
              defaultMessage: 'Show progress wheel',
            })}
            aria-checked={showProgress}
            role="switch"
            aria-label={intl.formatMessage({
              id: 'quiz_tooltip.progress',
              defaultMessage: 'Progress Toggle',
            })}
            isHidden={!user}
            icon={
              showProgress ? (
                <Image
                  className="mt-3"
                  src={ProgressBarBlue}
                  alt="Quiz Progress Icon"
                />
              ) : (
                <Image
                  className="mt-3"
                  src={ProgressBarGrey}
                  alt="Quiz Progress Icon"
                />
              )
            }
          />
          <IconButtonControl
            data-testid="lab-toggle"
            onClick={() => setShowLab(!showLab)}
            tooltipText={intl.formatMessage({
              id: 'quiz_tooltip.lab',
              defaultMessage: 'Show lab values',
            })}
            aria-label={intl.formatMessage({
              id: 'quiz_tooltip.lab',
              defaultMessage: 'lab values toggle',
            })}
            aria-checked={showLab}
            role="switch"
            isHidden={!user}
            icon={
              <Icon color={showLab ? 'teal' : ''} size="lg" icon={faFlask} />
            }
          />

          <IconButtonControl
            data-testid="highlight-toggle"
            onClick={() => setHighlighting(!highlighting)}
            tooltipText={intl.formatMessage({
              id: 'quiz_tooltip.highlight',
              defaultMessage: 'Highlight Mode',
            })}
            aria-label={intl.formatMessage({
              id: 'quiz_tooltip.highlight',
              defaultMessage: 'highlight mode toggle',
            })}
            aria-checked={highlighting}
            role="switch"
            isHidden={!user}
            icon={
              <Icon
                color={highlighting ? 'yellow' : ''}
                size="lg"
                icon={faPenAlt}
              />
            }
          />

          <IconButtonControl
            data-testid="size-toggle"
            aria-label={intl.formatMessage({
              id: 'quiz_tooltip.size',
              defaultMessage: 'Full Screen toggle',
            })}
            onClick={() => setFullQuizSize(!fullQuizSize)}
            aria-checked={fullQuizSize}
            role="switch"
            isHidden={!user}
            icon={
              <Icon
                size="lg"
                color={fullQuizSize ? 'red' : ''}
                icon={fullQuizSize ? faCompress : faExpand}
              />
            }
          />
        </Flex>
        <Flex flex={1.5}>
          <QuizNavigator onCloseLockoutModal={onCloseLockoutModal} />
        </Flex>
        <Flex flex={1} justifyContent="flex-end">
          <Button
            variant="outline"
            data-testid="settings"
            onClick={toggledSettingsMenu}
            startIcon={<Icon icon={faCog} />}
            className={`${text4}`}
          >
            <FormattedMessage id="common.settings" defaultMessage="Settings" />
          </Button>
          <Box
            style={{
              transform: 'translate(min(-25vw, -350px), 5vh)',
              zIndex: ZIndex.MODAL,
            }}
          >
            <ActionMenu
              keyExtractor={(item) => item.id}
              isVisible={settingsMenuVisible}
              hide={hideSettingsMenu}
              hideOnScroll={false}
              renderItem={(item) => <ActionMenuRow item={item} />}
              items={settingsPopupMenuItems}
              isPortal={false}
              customBoxStyle={{ maxHeight: '330px' }}
            ></ActionMenu>
          </Box>
        </Flex>
      </Flex>

      {user && <RepetitionSettingsModal />}
      <KeyboardShortcuts />
    </Box>
  );
};

export default QuizHeader;
